/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { SpannableTextProps } from '@airasia-phoenix/react-native/components/TextView';
import { pd } from '@airasia-phoenix/react-native/utils';
import { getTextStyle } from '@airasia-phoenix/react-native/utils/utils';
import { TFunction } from 'i18next';
import { maskEmail } from '../../utils/stringUtils';

type KeyedSpannableTextProps = SpannableTextProps & { key?: string };

export interface BuildVerifyEmailViewPropsOptions {
  resolvedEmail?: string;
  testID?: string;
}

export const buildVerifyEmailViewProps = (
  { resolvedEmail, testID }: BuildVerifyEmailViewPropsOptions,
  t: TFunction,
) => {
  const maskedEmail = resolvedEmail
    ? maskEmail(resolvedEmail, { style: 'otp' })
    : '';

  const subtitleTemplate: string = t('verifyEmailPage.subTitle');
  const countdownTemplate: string = t('verifyEmailPage.requestOtpCountdown');
  const requestNewOtpText: string = t('verifyEmailPage.requestNewOtpText');

  const root = testID ?? 'VerifyEmailView';

  return {
    title: t('verifyEmailPage.title'),
    subtitle: subtitleTemplate.replace('%s', maskedEmail),
    subtitleSpannableTextsProps: maskedEmail
      ? ([
          {
            spannable: maskedEmail,
            style: [
              getTextStyle(
                pd.sys.typescale.body_text_medium,
                pd.sys.colour.content_primary,
              ),
            ],
          },
        ] as SpannableTextProps[])
      : undefined,

    timer: {
      headerText: t('verifyEmailPage.didntGetOtp'),
      getTimerText: (timer: number) =>
        timer > 0
          ? countdownTemplate.replace('%s', `${timer}`)
          : requestNewOtpText,
      getTimerSpannableTextsProps: (
        timer: number,
      ): KeyedSpannableTextProps[] =>
        timer === 0
          ? [
              {
                key: 'verify-email-request-new-otp',
                spannable: requestNewOtpText,
                style: [
                  getTextStyle(
                    pd.sys.typescale.sub_body_text_medium,
                    pd.sys.colour.interactive_link,
                  ),
                ],
              },
            ]
          : [],
    },

    alternativeSignUpText: t('verifyEmailPage.alternativeSignUpDescription'),

    headerTestID: root,

    testIDs: {
      error: `${root}-ErrorNotification`,
    },
  };
};
